/**
 * auto-add-free-filter.js
 *
 * When a Paws Fountain variant is added to cart, automatically adds 2× Starter
 * Filter units (the $0 INTERNAL variant) as an included free item — no selling_plan,
 * no subscription.
 *
 * When the fountain is subsequently removed, the free filter line is zeroed out too.
 *
 * Hook: patches window.fetch to intercept /cart/add.js, /cart/change.js, /cart/update.js.
 * Both pf-pdp-hero.liquid (custom) and Dawn's product-form.js use plain window.fetch,
 * so this interceptor catches all add-to-cart and remove-from-cart events on this theme.
 *
 * FILE RENAME NOTE: previously auto-add-subscription.js — renamed because this phase
 * is free one-time filter only; subscription/selling_plan logic deferred to a later phase.
 */
(function () {
  'use strict';

  /* ── Real variant IDs (confirmed via GraphQL 2025-06-30) ───────────── */
  var FOUNTAIN_VARIANT_IDS = ['48032713212078', '48032713244846']; // 4L, 7L
  var FREE_FILTER_VARIANT_ID = '48032843366574';                   // "2 Filters - INTERNAL" $0
  var FREE_FILTER_QUANTITY   = 2;

  var isAutoAdding = false;
  var originalFetch = window.fetch.bind(window);

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  function getUrl(resource) {
    if (typeof resource === 'string') return resource;
    if (resource && resource.url) return resource.url;
    return '';
  }

  function hasFountainInItems(data) {
    if (!data) return false;
    if (FOUNTAIN_VARIANT_IDS.includes(String(data.id))) return true;
    if (Array.isArray(data.items)) {
      return data.items.some(function (item) {
        return FOUNTAIN_VARIANT_IDS.includes(String(item.variant_id));
      });
    }
    return false;
  }

  /* ── Add free filter ─────────────────────────────────────────────────── */
  function addFreeFilter() {
    return originalFetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(FREE_FILTER_VARIANT_ID, 10), quantity: FREE_FILTER_QUANTITY }]
      })
    }).then(function (r) {
      if (!r.ok) throw new Error('free-filter add returned ' + r.status);
    });
  }

  /* ── Refresh the open cart drawer with up-to-date sections ──────────── */
  function refreshCartDrawer() {
    return originalFetch('/cart?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (sections) {
        if (!sections) return;
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents({ sections: sections });
        }
      })
      .catch(function () {});
  }

  /* ── Add filter in background then refresh drawer ────────────────────── */
  function addFreeFilterAndRefresh() {
    isAutoAdding = true;
    addFreeFilter()
      .catch(function (e) {
        console.warn('[pf] Free-filter auto-add failed (fountain purchase unaffected):', e);
      })
      .then(function () {
        isAutoAdding = false;
        return refreshCartDrawer();
      })
      .catch(function () { isAutoAdding = false; });
  }

  /* ── Remove free filter if fountain leaves cart ──────────────────────── */
  function syncFreeFilterRemoval() {
    return originalFetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var fountainPresent = cart.items.some(function (item) {
          return FOUNTAIN_VARIANT_IDS.includes(String(item.variant_id));
        });
        if (fountainPresent) return; // fountain still here, nothing to do

        var freeItem = cart.items.find(function (item) {
          return String(item.variant_id) === FREE_FILTER_VARIANT_ID;
        });
        if (!freeItem) return; // free filter not in cart either

        isAutoAdding = true;
        return originalFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: freeItem.key, quantity: 0 })
        }).finally(function () { isAutoAdding = false; });
      })
      .catch(function (e) {
        console.warn('[pf] Could not sync free-filter removal:', e);
      });
  }

  /* ── Fetch interceptor ───────────────────────────────────────────────── */
  window.fetch = function () {
    var args = Array.prototype.slice.call(arguments);
    var resource = args[0];
    var config   = args[1] || {};
    var url      = getUrl(resource);
    var method   = (config.method || 'GET').toUpperCase();

    /* ── /cart/add.js — pass through immediately, add filter + refresh in background ── */
    if (url.includes('/cart/add') && method === 'POST' && !isAutoAdding) {
      return originalFetch.apply(window, args).then(function (response) {
        var clone = response.clone();
        clone.json().catch(function () { return null; }).then(function (data) {
          if (hasFountainInItems(data)) {
            addFreeFilterAndRefresh(); // fire-and-forget
          }
        });
        return response; // return original response immediately so drawer opens
      });
    }

    /* ── /cart/change.js or /cart/update.js — watch for fountain removal ── */
    if ((url.includes('/cart/change') || url.includes('/cart/update')) && method === 'POST' && !isAutoAdding) {
      return originalFetch.apply(window, args).then(function (response) {
        syncFreeFilterRemoval(); // fire-and-forget — don't block cart UI
        return response;
      });
    }

    /* All other requests — pass through */
    return originalFetch.apply(window, args);
  };
})();
